import React from 'react';
import styled from 'styled-components';

import { NAME_COL_WIDTH } from '../ScheduleCoordination/constants';

/**
 * 店長排班預覽的長條月表：員工為列、日期為欄（D1，沿用 task-8905 已驗收的形狀）。
 *
 * 表格由上到下四段，全部在同一張表裡：
 *   ① 表頭　　　日 ＋ 星期；有硬性違規的欄底加橘線
 *   ② 開診列　　單／雙／休／?（未設定，橘色）
 *   ③ 員工列　　班別與假別寫在同一格；機動補位顯示「機動→○○」
 *   ④ 彙總列　　各職能「在班／需求」＋ 規則檢核（✓／✕硬／補）
 *
 * 桌機優先：姓名欄 sticky 在左、日期欄固定寬度不換行，
 * 所以外層 overflow-x:auto、table 用 width:max-content。
 */
const Scroller = styled.div`
    width: 100%;
    overflow-x: auto;
    background-color: #fff;
`;

const Table = styled.table`
    width: max-content;
    min-width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 12px;

    th,
    td {
        border-right: 1px solid #eceff1;
        border-bottom: 1px solid #eceff1;
        text-align: center;
        vertical-align: middle;
        white-space: nowrap;
    }

    thead th {
        position: sticky;
        top: 0;
        z-index: 20;
        background-color: #f8f9fa;
        color: #4b5563;
        font-weight: bold;
        padding: 6px 2px;
        height: 44px;
        border-bottom: 1px solid #e2e6ea;
    }

    .col-name {
        position: sticky;
        left: 0;
        z-index: 30;
        background-color: #f8f9fa;
        width: ${NAME_COL_WIDTH + 60}px;
        min-width: ${NAME_COL_WIDTH + 60}px;
        text-align: left;
        padding-left: 10px;
        border-right: 1px solid #cfd6dd;
        box-shadow: 2px 0 0 rgba(0, 0, 0, 0.02);
    }

    tbody .col-name {
        z-index: 10;
        background-color: #fff;
    }

    tbody tr.section-head .col-name {
        background-color: #eef1f4;
        font-weight: bold;
    }

    tbody tr.section-head td {
        background-color: #fbfcfd;
    }

    /* ⚠️ 固定寬度 ＋ overflow:hidden：格子裡的長字串（例如補位職能名稱）
       不可以把整欄撐開，否則整張長條月表的欄寬會忽大忽小。完整內容放 title。 */
    .day-col {
        width: 48px;
        min-width: 48px;
        max-width: 48px;
        padding: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .weekday {
        font-size: 10px;
        color: #78909c;
        line-height: 1.2;
    }

    .day-num {
        font-size: 13px;
        font-weight: bold;
        line-height: 1.2;
    }

    .is-weekend {
        background-color: #fafafa;
    }

    thead th.is-weekend {
        background-color: #eef1f4;
    }

    /* 有硬性違規的日期欄：欄底加橘線（D1 圖②） */
    thead th.has-hard {
        box-shadow: inset 0 -3px 0 #e8590c;
        color: #d9480f;
    }

    .clinic-cell {
        font-weight: bold;
        height: 26px;
    }

    .clinic-dual {
        background-color: #e7f1fb;
        color: #1864ab;
    }

    .clinic-unset {
        color: #d9480f;
    }

    .member-cell {
        height: 36px;
    }

    /* 造成硬性違規的那一格：加粗橘框 */
    .member-cell.blames {
        outline: 2px solid #e8590c;
        outline-offset: -2px;
    }

    .cover-tag {
        display: block;
        font-size: 9px;
        line-height: 1.2;
        color: #00695c;
        font-weight: bold;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* 職能列：上下午人數相同時只出現一個大字（.count-one），
       不同時才拆成兩行、而且一定帶「上／下」標籤（.count-line）。
       ⛔ 不要回到「兩個沒有標籤的數字疊在一起」—— 佳儀 2026-09-14 的回饋就是那樣看不下去。 */
    .count-one {
        display: block;
        font-size: 13px;
        font-weight: bold;
        line-height: 32px;
    }

    .count-line {
        display: block;
        font-size: 10px;
        line-height: 1.25;
        white-space: nowrap;

        i {
            font-style: normal;
            color: #78909c;
            margin-right: 1px;
        }
    }

    /* 缺額只落在單一時段時，只有那一行變紅 —— ⛔ 不可以因為合併顯示就把半天缺額蓋掉 */
    .count-line.is-short,
    .count-one.is-short {
        color: #d9480f;
        font-weight: bold;
    }

    .count-cell {
        height: 34px;
        padding: 1px 0;
    }

    .count-short {
        background-color: #fff4e6;
        outline: 1px solid #f0a760;
        outline-offset: -1px;
        color: #d9480f;
    }
`;

const Chip = styled.span`
    display: inline-block;
    min-width: 30px;
    padding: 1px 3px;
    border-radius: 3px;
    font-size: 10px;
    line-height: 1.3;
    color: #fff;
    background-color: ${(props) => props.color};
`;

/** 一格的短標籤：休／特 ＋ AM/PM；沒劃假就是「正」（上班） */
const markLabel = (mark) => {
    if (!mark) return null;
    const base = mark.category === 'annual_leave' ? '特' : '休';
    if (mark.period === 'full') return base;
    return base + (mark.period === 'am' ? 'AM' : 'PM');
};

const SESSIONS = ['am', 'pm'];
const SESSION_LABEL = { am: '上午', pm: '下午' };
const SESSION_SHORT = { am: '上', pm: '下' };

/** 一個時段的顯示字串：有需求就寫「在班/需求」，沒有就只寫在班人數 */
const countText = (cell) => (cell.need != null ? `${cell.actual}/${cell.need}` : `${cell.actual}`);

const PreviewTable = ({ payload, onDeficitClick }) => {
    const dates = payload.dates || [];
    const members = payload.members || [];
    const skills = payload.skills || [];
    const counts = payload.skill_counts || {};
    const required = payload.required || {};
    const covers = payload.covers || {};
    const floaters = payload.floaters || [];
    const clinicShort = payload.clinic_short || {};

    // 哪些日期有硬性違規／柔性違規／待補資料 —— 表頭與檢核列共用
    const dayFlags = {};
    (payload.results || []).forEach((r) => {
        const flag = dayFlags[r.date] || { hard: 0, soft: 0, pending: 0, checked: 0 };
        flag.checked += 1;
        if (!(r.passed && !r.is_pending_data)) {
            if (r.is_pending_data) flag.pending += 1;
            else if (r.enforcement === 'hard') flag.hard += 1;
            else flag.soft += 1;
        }
        dayFlags[r.date] = flag;
    });

    // ⚠️ 「沒有任何規則適用」跟「全部通過」絕對不可以畫成同一個綠色 ✓。
    //    2026-10-17 兩位諮詢師整天都休假卻顯示通過，就是因為那條規則的有效期間
    //    停在 2027 年、根本沒被套用，而畫面把「零結果」當成了「通過」。
    const daySkips = {};
    (payload.skips || []).forEach((sk) => {
        daySkips[sk.date] = daySkips[sk.date] || [];
        daySkips[sk.date].push(sk.message);
    });

    // 哪個人的哪一格造成了硬性違規：該日有硬性缺額、且這個人當天有劃假
    const blames = {};
    (payload.results || []).forEach((r) => {
        if (r.passed || r.is_pending_data || r.enforcement !== 'hard') return;
        members.forEach((m) => {
            const hasMark = (m.marks || []).some((x) => x.date === r.date);
            if (hasMark) blames[`${m.uuid}|${r.date}`] = true;
        });
    });

    return (
        <Scroller>
            <Table>
                <thead>
                    <tr>
                        <th className="col-name">姓名 / 檢核項目</th>
                        {dates.map((d) => {
                            const flag = dayFlags[d.date] || {};
                            return (
                                <th
                                    key={d.date}
                                    className={`day-col${d.is_weekend ? ' is-weekend' : ''}${flag.hard ? ' has-hard' : ''}`}
                                    title={d.date}
                                >
                                    <div className="weekday">{d.weekday}</div>
                                    <div className="day-num">{d.day}</div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {/* ② 開診列 */}
                    <tr className="section-head">
                        <td className="col-name">開診（整日）</td>
                        {dates.map((d) => {
                            const am = ((d.clinic || {}).am || {}).mode;
                            const pm = ((d.clinic || {}).pm || {}).mode;
                            const cls = (mode) =>
                                mode === 'dual' ? 'clinic-dual' : mode === 'unset' ? 'clinic-unset' : '';
                            // 醫師以整日計算，am/pm 一律同值 → 只寫一個字。
                            // ⚠️ 舊資料若真的上下午不同，仍如實寫成「單/雙」，⛔ 不挑一邊假裝成整日值。
                            const same = am === pm;
                            const labelOf = (mode) => (payload.clinic_labels || {})[mode] || '未設定';
                            return (
                                <td
                                    key={d.date}
                                    className={`day-col clinic-cell ${cls(am)}`}
                                    title={
                                        same
                                            ? `${d.date} 當日：${labelOf(am)}`
                                            : `${d.date} 舊資料 上午：${labelOf(am)}／下午：${labelOf(pm)}`
                                    }
                                >
                                    {same ? (
                                        clinicShort[am] || '?'
                                    ) : (
                                        <React.Fragment>
                                            {clinicShort[am] || '?'}
                                            {'/'}
                                            {clinicShort[pm] || '?'}
                                        </React.Fragment>
                                    )}
                                </td>
                            );
                        })}
                    </tr>

                    {/* ③ 員工列 */}
                    {members.map((member) => {
                        const marksByDate = {};
                        (member.marks || []).forEach((mark) => {
                            marksByDate[mark.date] = mark;
                        });
                        const isFloater = floaters.indexOf(member.uuid) >= 0;

                        return (
                            <tr key={member.uuid}>
                                <td className="col-name">
                                    {member.name}
                                    {isFloater && (
                                        <span
                                            style={{
                                                fontSize: '10px',
                                                marginLeft: '6px',
                                                color: '#00695c',
                                                border: '1px solid #80cbc4',
                                                borderRadius: '8px',
                                                padding: '0 5px',
                                            }}
                                        >
                                            機動
                                        </span>
                                    )}
                                </td>
                                {dates.map((d) => {
                                    const mark = marksByDate[d.date];
                                    const label = markLabel(mark);
                                    const memberCovers = (covers[member.uuid] || {})[d.date] || {};
                                    const coverAm = memberCovers.am;
                                    const coverPm = memberCovers.pm;
                                    // 格子只放得下「機動→○」，完整職能名稱放 title
                                    const shortSkill = (c) => (c && c.skill_name ? c.skill_name.replace(/^【[^】]*】/, '').slice(0, 2) : '');
                                    const coverShort =
                                        coverAm && coverPm && coverAm.skill_id === coverPm.skill_id
                                            ? `機動→${shortSkill(coverAm)}`
                                            : [
                                                  coverAm ? `AM→${shortSkill(coverAm)}` : null,
                                                  coverPm ? `PM→${shortSkill(coverPm)}` : null,
                                              ]
                                                  .filter(Boolean)
                                                  .join(' ');
                                    const coverText = [
                                        coverAm ? `上午補「${coverAm.skill_name}」` : null,
                                        coverPm ? `下午補「${coverPm.skill_name}」` : null,
                                    ]
                                        .filter(Boolean)
                                        .join('、');

                                    return (
                                        <td
                                            key={`${member.uuid}|${d.date}`}
                                            className={`day-col member-cell${d.is_weekend ? ' is-weekend' : ''}${
                                                blames[`${member.uuid}|${d.date}`] && mark ? ' blames' : ''
                                            }`}
                                            title={`${member.name} ${d.date} ${
                                                mark ? `${mark.category_label}・${mark.period_label}` : '上班'
                                            }${coverText ? ` / 機動補位 ${coverText}` : ''}`}
                                        >
                                            {label ? (
                                                <Chip color={mark.color}>{label}</Chip>
                                            ) : (
                                                <span style={{ color: '#cfd8dc' }}>正</span>
                                            )}
                                            {coverShort && <span className="cover-tag">{coverShort}</span>}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}

                    {/* ④ 彙總列：各職能在班／需求 */}
                    {skills.map((skill) => (
                        <tr key={`skill-${skill.id}`} className="section-head">
                            <td className="col-name">{skill.name} 在班</td>
                            {dates.map((d) => {
                                const dayCounts = counts[d.date] || {};
                                const reqDay = required[d.date] || {};
                                const clinic = d.clinic || {};

                                const all = SESSIONS.map((session) => {
                                    const actual = ((dayCounts[session] || {}).on_duty || {})[skill.id] || 0;
                                    const need = (reqDay[session] || {})[skill.id];
                                    return {
                                        session,
                                        actual,
                                        need,
                                        closed: ((clinic[session] || {}).mode) === 'none',
                                        short: need != null && actual < need,
                                    };
                                });

                                // 休診的時段沒有在班語意，⛔ 不製造不存在的另一筆。
                                // 兩個時段都休診時仍只顯示一格（不是隱藏整格）。
                                const open = all.filter((c) => !c.closed);
                                const cells = open.length ? open : [all[0]];
                                const merged =
                                    // 全天休診：一個數字就夠了，⛔ 不用標時段
                                    open.length === 0 ||
                                    (cells.length === 2 &&
                                        cells[0].actual === cells[1].actual &&
                                        cells[0].need === cells[1].need);
                                const short = cells.some((c) => c.short);

                                const title = all
                                    .map((c) =>
                                        c.closed
                                            ? `${d.date} ${SESSION_LABEL[c.session]} 休診`
                                            : `${d.date} ${
                                                  merged ? '全天' : SESSION_LABEL[c.session]
                                              }在班 ${c.actual} 人${
                                                  c.need != null
                                                      ? `／需求 ${c.need} 人${
                                                            c.short ? `（缺 ${c.need - c.actual} 人）` : ''
                                                        }`
                                                      : ''
                                              }`
                                    )
                                    .filter((text, i) => !(merged && i > 0))
                                    .join('　');

                                return (
                                    <td
                                        key={d.date}
                                        className={`day-col count-cell${short ? ' count-short' : ''}`}
                                        title={title}
                                    >
                                        {merged ? (
                                            <span className={`count-one${cells[0].short ? ' is-short' : ''}`}>
                                                {countText(cells[0])}
                                            </span>
                                        ) : (
                                            cells.map((c) => (
                                                <span
                                                    className={`count-line${c.short ? ' is-short' : ''}`}
                                                    key={c.session}
                                                >
                                                    <i>{SESSION_SHORT[c.session]}</i>
                                                    {countText(c)}
                                                </span>
                                            ))
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}

                    {/* ④ 檢核列 */}
                    <tr className="section-head">
                        <td className="col-name">規則檢核</td>
                        {dates.map((d) => {
                            const flag = dayFlags[d.date] || {};
                            const skips = daySkips[d.date] || [];
                            let text = '✓';
                            let color = '#2b8a3e';
                            let title = `${d.date}　${flag.checked || 0} 條規則檢查通過`;
                            // 有規則沒被套用時，✓ 後面加一個星號提醒「不是全部規則都在看這一天」
                            const partial = !!flag.checked && skips.length > 0;

                            if (flag.hard) {
                                text = '✕硬';
                                color = '#c92a2a';
                            } else if (flag.soft) {
                                text = '△';
                                color = '#d9480f';
                            } else if (flag.pending) {
                                text = '補';
                                color = '#d9480f';
                            } else if (!flag.checked) {
                                // 零條規則被套用 → 明示「沒有規則在看這一天」，⛔ 不可以顯示通過
                                text = '—';
                                color = '#adb5bd';
                                title = `${d.date}　這一天沒有任何規則被套用${
                                    skips.length ? `：\n${skips.join('\n')}` : '（規則庫裡沒有涵蓋這一天的規則）'
                                }`;
                            }

                            if (flag.checked && skips.length) {
                                title += `\n未被套用的規則：\n${skips.join('\n')}`;
                            }

                            return (
                                <td
                                    key={d.date}
                                    className="day-col count-cell"
                                    style={{ color, cursor: flag.hard || flag.soft ? 'pointer' : 'default' }}
                                    title={title}
                                    onClick={
                                        flag.hard || flag.soft ? () => onDeficitClick && onDeficitClick(d.date) : undefined
                                    }
                                >
                                    {text}
                                    {partial && !flag.hard && !flag.soft && !flag.pending && (
                                        <span style={{ color: '#adb5bd', fontSize: '10px' }}>＊</span>
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                </tbody>
            </Table>
        </Scroller>
    );
};

export default PreviewTable;
